import Reveal from '@/components/base/Reveal';

interface TechStat {
  value: string;
  label: string;
}

interface TechImagePanelProps {
  imageUrl: string;
  imageAlt: string;
  eyebrow?: string;
  title?: string;
  desc?: string;
  chips?: string[];
  stats?: TechStat[];
  dark?: boolean;
}

export default function TechImagePanel({
  imageUrl,
  imageAlt,
  eyebrow,
  title,
  desc,
  chips,
  stats,
  dark = false,
}: TechImagePanelProps) {
  return (
    <section className={`w-full ${dark ? 'bg-foreground-950' : 'bg-background-50'} py-8 md:py-12`}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <Reveal>
          <div
            className={`relative rounded-lg overflow-hidden border group ${
              dark ? 'border-foreground-700' : 'border-background-200'
            } min-h-[340px] md:min-h-[420px]`}
          >
            <img
              src={imageUrl}
              alt={imageAlt}
              title={imageAlt}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/70 to-foreground-950/25"></div>
            <div className="absolute inset-0 animate-shimmer pointer-events-none"></div>
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-primary-500/25 blur-3xl animate-glow-pulse pointer-events-none"></div>
            <div
              className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-accent-500/20 blur-3xl animate-glow-pulse pointer-events-none"
              style={{ animationDelay: '1.5s' }}
            ></div>

            <div className="relative z-10 flex flex-col justify-center h-full min-h-[340px] md:min-h-[420px] p-8 md:p-12">
              <div className="max-w-xl">
                {eyebrow && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 border border-background-50/20 backdrop-blur">
                    <span className="w-2 h-2 rounded-full bg-accent-400"></span>
                    <span className="text-xs text-background-100 whitespace-nowrap">{eyebrow}</span>
                  </div>
                )}
                {title && (
                  <h2 className="mt-4 font-heading font-bold text-2xl md:text-4xl text-background-50 leading-snug">
                    {title}
                  </h2>
                )}
                {desc && (
                  <p className="mt-3 text-sm md:text-base text-foreground-300 leading-relaxed">{desc}</p>
                )}
                {chips && chips.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {chips.map((c) => (
                      <span
                        key={c}
                        className="px-3 py-1.5 text-xs rounded-full bg-background-50/10 border border-background-50/20 text-background-100 whitespace-nowrap"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {stats && stats.length > 0 && (
                <div className="mt-8 flex flex-wrap gap-3">
                  {stats.map((s) => (
                    <div
                      key={s.label}
                      className="px-4 py-3 rounded-lg bg-background-50/10 border border-background-50/15 backdrop-blur"
                    >
                      <div className="font-heading font-bold text-lg md:text-xl text-primary-300">
                        {s.value}
                      </div>
                      <div className="text-xs text-foreground-300">{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}